import { ExpenseDetails } from '@/models/expense-details/expense-details';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subject, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-expense-master',
  templateUrl: './expense-master.component.html',
  styleUrl: './expense-master.component.scss'
})
export class ExpenseMasterComponent  implements OnInit, OnDestroy {

  isEditMode = false;
  isSubmitting = false;

  private destroy$ = new Subject<void>();
  expenseDetails: ExpenseDetails[] = [];
  private searchSubject = new Subject<string>();
  searchText = '';
  selectedStatus = 'ACTIVE';

  listEmployeeStatus: string[] = ['ACTIVE', 'INACTIVE'];

  expenseForm = new UntypedFormGroup({
    expenseSeq: new UntypedFormControl(''),
    name: new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(100)
    ]),
    status: new UntypedFormControl('ACTIVE'),
    createdAt: new UntypedFormControl(0),
    createdBy: new UntypedFormControl(0),
    updatedAt: new UntypedFormControl(0),
    updatedBy: new UntypedFormControl(0)
  });

  expenseErrors: { name: string } = {
    name: ''
  };

  constructor(
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.getAllExpenseDetails();
  }

  // Load all expense master records
  getAllExpenseDetails(): void {
    this.spinner.show();

    this.appService.getAllExpenseDetails()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide())
      )
      .subscribe({
        next: (response: ExpenseDetails[]) => {
          this.expenseDetails = [...(response || [])].sort(
            (a, b) => (b.expenseSeq || 0) - (a.expenseSeq || 0)
          );
        },
        error: (err: any) => {
          this.toastr.error(
            err?.error?.message ||
            'Failed to load expense details. Please try again.'
          );
        }
      });
  }

  // Validate form before save/update
  expenseFormValidation(): void {
    this.expenseErrors = { name: '' };

    const nameControl = this.expenseForm.get('name');

    if (nameControl?.hasError('required')) {
      this.expenseErrors.name = 'Expense Name is required';
    } else if (nameControl?.hasError('maxlength')) {
      this.expenseErrors.name =
        'Expense Name cannot exceed 100 characters';
    }

    if (this.expenseForm.invalid) {
      this.expenseForm.markAllAsTouched();
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.saveAndUpdateExpenseDetails();
  }

  // Save new expense or update existing expense
  saveAndUpdateExpenseDetails(): void {
    if (this.isSubmitting) {
      return;
    }

    const isUpdate = this.isEditMode;
    const action = isUpdate ? 'updated' : 'saved';

    const formData = {
      ...this.expenseForm.getRawValue(),
      name: String(this.expenseForm.get('name')?.value || '').trim()
    };

    if (!isUpdate) {
      formData.status = 'ACTIVE';
    }

    this.isSubmitting = true;
    this.spinner.show();

    const request$ = isUpdate
      ? this.appService.updateExpenseDetail(formData)
      : this.appService.saveExpenseDetail(formData);

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.spinner.hide();
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success(`Expense ${action} successfully.`);
          this.resetExpenseForm();
          this.getAllExpenseDetails();
        },
        error: (err: any) => {
          this.toastr.error(
            err?.error?.message ||
            `Expense could not be ${action}. Please try again.`
          );
        }
      });
  }

  // Populate form for editing
  editExpenseDetails(expense: ExpenseDetails): void {
    if (!expense) {
      return;
    }

    this.isEditMode = true;
    this.expenseErrors = { name: '' };

    this.expenseForm.patchValue({
      expenseSeq: expense.expenseSeq ?? '',
      name: expense.name ?? '',
      status: expense.status ?? 'ACTIVE',
      createdAt: expense.createdAt ?? 0,
      createdBy: expense.createdBy ?? 0,
      updatedAt: expense.updatedAt ?? 0,
      updatedBy: expense.updatedBy ?? 0
    });

    this.expenseForm.get('name')?.markAsUntouched();
  }

  // Cancel edit or clear form
  cancelForm(): void {
    this.resetExpenseForm();
  }

  private resetExpenseForm(): void {
    this.isEditMode = false;
    this.expenseErrors = { name: '' };

    this.expenseForm.reset({
      expenseSeq: '',
      name: '',
      status: 'ACTIVE',
      createdAt: 0,
      createdBy: 0,
      updatedAt: 0,
      updatedBy: 0
    });
  }

  // Enable/disable expense master
  updateExpenseToggle(
    expense: ExpenseDetails,
    index: number,
    event: Event
  ): void {
    const checkbox = event.target as HTMLInputElement;
    const previousStatus = expense.status;
    const newStatus = checkbox.checked ? 'ACTIVE' : 'INACTIVE';

    if (previousStatus === newStatus) {
      return;
    }

    // Optimistically update the UI
    this.expenseDetails[index] = {
      ...expense,
      status: newStatus
    };

    checkbox.disabled = true;
    this.spinner.show();

    const request = {
      expenseSeq: expense.expenseSeq || 0,
      status: newStatus
    };

    this.appService.updateExpenseStatus(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.spinner.hide();
          checkbox.disabled = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            `Expense status updated to ${
              newStatus === 'ACTIVE' ? 'Active' : 'Inactive'
            }.`
          );

          this.getAllExpenseDetails();
        },
        error: (err: any) => {
          // Restore the original status if API fails
          this.expenseDetails[index] = {
            ...expense,
            status: previousStatus
          };

          checkbox.checked = previousStatus === 'ACTIVE';

          this.toastr.error(
            err?.error?.message ||
            'Failed to update expense status.'
          );
        }
      });
  }

  // Search + status filter
get filteredExpenses(): ExpenseDetails[] {
  const search = (this.searchText || '').trim().toLowerCase();
  const status = (this.selectedStatus || '').trim().toUpperCase();

  return (this.expenseDetails || []).filter((expense) => {
    const expenseName = (expense.name || '').toLowerCase();
    const expenseStatus = (expense.status || '').trim().toUpperCase();

    const matchesSearch = expenseName.includes(search);
    const matchesStatus = !status || expenseStatus === status;

    return matchesSearch && matchesStatus;
  });
}

// Update search text
onSearchChange(event: Event): void {
  this.searchText = (event.target as HTMLInputElement).value;
}

// Reset search and filter
resetFilters(): void {
  this.searchText = '';
  this.selectedStatus = 'ACTIVE';
}
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}